import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFilters } from '../contexts/FilterContext';
import { predictiveMaintenanceAPI, equipmentAPI } from '../services/api';

// Helper to format dates for API
const formatDateForApi = (date) => {
  if (!(date instanceof Date)) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PredictiveMaintenance = ({ activeTab = 'Predictive Maintenance' }) => {
  const { filters, manualTrigger, setAvailableMachines } = useFilters();

  // Track if Predictive Maintenance component has loaded its initial data (LOCAL state, not global)
  const [predictiveInitialDataLoaded, setPredictiveInitialDataLoaded] = useState(false);

  // Track previous manualTrigger to detect when arrow button is clicked
  const prevManualTriggerRef = useRef(manualTrigger);

  // Track previous activeTab to detect when Predictive Maintenance tab becomes active
  const prevActiveTabRef = useRef(activeTab);

  // Track if API call is in progress to prevent duplicate calls
  const isFetchingRef = useRef(false);

  // Track if initial call has been made to prevent duplicate initial calls
  const initialCallMadeRef = useRef(false);

  const [loading, setLoading] = useState(false);

  // State for predictive maintenance data
  const [keyMetrics, setKeyMetrics] = useState({
    remainingUsefulLife: { value: 0, unit: 'Nos' },
    costToOrder: { value: 0, unit: 'Price', suffix: '' },
    numberOfSuppliers: { value: 0, unit: 'Nos' },
    numberOfSpares: { value: 0, unit: 'Nos' }
  });
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedSupplierEquipmentId, setSelectedSupplierEquipmentId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Track previous selectedSupplier to detect changes
  const prevSelectedSupplierRef = useRef(selectedSupplier);
  const prevSelectedSupplierEquipmentIdRef = useRef(selectedSupplierEquipmentId);

  // Track previous selectedItem to detect changes
  const prevSelectedItemRef = useRef(selectedItem);

  // Fetch predictive maintenance data from API
  const fetchPredictiveMaintenanceData = useCallback(async () => {
    // Only fetch if Predictive Maintenance is the active tab
    if (activeTab !== 'Predictive Maintenance') {
      console.log('⏸️ Skipping Predictive Maintenance API call - not active tab');
      return;
    }

    // Prevent duplicate calls
    if (isFetchingRef.current) {
      console.log('⏸️ API call already in progress, skipping duplicate request');
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      console.log('🔄 Fetching predictive maintenance data...');

      // Get date range from filters or use default (this month start to today)
      const today = new Date();
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

      const startDate = filters.dateRange?.from
        ? formatDateForApi(filters.dateRange.from)
        : formatDateForApi(thisMonthStart);
      const endDate = filters.dateRange?.to
        ? formatDateForApi(filters.dateRange.to)
        : formatDateForApi(today);

      // Build API filters - only include selected filters
      const apiFilters = {};

      // Add company ID if selected (required)
      if (filters.selectedCompany) {
        apiFilters.companyId = filters.selectedCompany;
      }

      // Add date range if both dates are selected
      if (startDate && endDate) {
        apiFilters.fromDate = startDate;
        apiFilters.toDate = endDate;
      }

      // Add optional filters ONLY if they are selected (not empty/null/undefined)
      if (filters.selectedLocation) {
        apiFilters.locationId = filters.selectedLocation;
        console.log('📍 Location filter:', filters.selectedLocation);
      }
      if (filters.selectedMachine) {
        apiFilters.equipmentId = filters.selectedMachine;
        console.log('🔧 Machine filter:', filters.selectedMachine, '(mapped to equipmentId)');
      }

      // Add equipment_id filter if selected via supplier drill-down
      if (selectedSupplierEquipmentId) {
        apiFilters.equipmentId = selectedSupplierEquipmentId;
        console.log('🏭 Supplier drill-down (equipment_id):', selectedSupplierEquipmentId);
      }

      // Add item filter if selected via drill-down
      if (selectedItem) {
        apiFilters.item_name = selectedItem;
        console.log('📦 Item filter (drill-down):', selectedItem);
      }
      // Note: shift filter is NOT included for predictive maintenance

      console.log('📤 Predictive Maintenance API filters:', apiFilters);
      console.log('📋 All filters from context:', {
        selectedCompany: filters.selectedCompany,
        selectedLocation: filters.selectedLocation,
        selectedMachine: filters.selectedMachine
      });

      const response = await predictiveMaintenanceAPI.getPredictive(apiFilters);

      if (response && response.success) {
        console.log('✅ Predictive maintenance data received:', response);

        // Extract and transform summary data
        if (response.summary) {
          setKeyMetrics({
            remainingUsefulLife: {
              value: response.summary.remainingUsefulLife?.value || 0,
              unit: response.summary.remainingUsefulLife?.unit || 'Nos'
            },
            costToOrder: {
              value: response.summary.costToOrder?.value || 0,
              unit: response.summary.costToOrder?.unit || 'Price',
              suffix: ''
            },
            numberOfSuppliers: {
              value: response.summary.numberOfSuppliers?.value || 0,
              unit: response.summary.numberOfSuppliers?.unit || 'Nos'
            },
            numberOfSpares: {
              value: response.summary.numberOfSpares?.value || 0,
              unit: response.summary.numberOfSpares?.unit || 'Nos'
            }
          });
          console.log('✅ Key metrics updated:', response.summary);
        }

        // Extract and transform tables data
        if (response.tables) {
          // Transform items table
          let transformedItems = [];
          if (response.tables.items && Array.isArray(response.tables.items)) {
            transformedItems = response.tables.items.map(item => ({
              itemName: item.item_name || '',
              reorderStatus: item.should_reorder || 'No Reorder',
              qty: item.usage_count || 0,
              charge: item.cost_to_order || 0,
              supplierName: item.supplier_name || '' // Keep supplier_name for equipment extraction
            }));
            setItems(transformedItems);
            console.log('✅ Items transformed:', transformedItems.length, 'items');
          }

          // Transform suppliers table
          let transformedSuppliers = [];
          if (response.tables.suppliers && Array.isArray(response.tables.suppliers)) {
            transformedSuppliers = response.tables.suppliers.map(supplier => ({
              supplierName: supplier.supplier_name || '',
              equipmentId: supplier.equipment_id || null,
              reorderStatus: supplier.should_reorder || 'No Reorder',
              qty: supplier.quantity || 0,
              amount: supplier.order_amount || 0
            }));
            setSuppliers(transformedSuppliers);
            console.log('✅ Suppliers transformed:', transformedSuppliers.length, 'suppliers');
          }

          // Update available machines from equipment_ids in the response
          const equipmentIds = response.equipment_ids || [];
          if (equipmentIds.length > 0) {
            // Cross-reference with equipment API to get proper names
            const companyId = filters.selectedCompany ||
              localStorage.getItem('companyId') || sessionStorage.getItem('companyId');

            if (companyId) {
              try {
                const fullEquipmentList = await equipmentAPI.getEquipment(companyId);
                if (fullEquipmentList && Array.isArray(fullEquipmentList)) {
                  // Filter full list to only those IDs in the response
                  const equipmentIdSet = new Set(equipmentIds.map(id => id.toString()));
                  const filteredMachines = fullEquipmentList.filter(eq =>
                    equipmentIdSet.has(eq.machineId.toString())
                  );

                  if (filteredMachines.length > 0) {
                    setAvailableMachines(filteredMachines);
                    console.log('✅ Predictive: Updated available machines with names:', filteredMachines.length);
                  } else {
                    // Fallback: use IDs directly
                    const machinesFromIds = equipmentIds.map(id => ({
                      machineId: id,
                      machineName: `Equipment ${id}`
                    }));
                    setAvailableMachines(machinesFromIds);
                    console.log('✅ Predictive: Fallback - using equipment IDs:', machinesFromIds.length);
                  }
                }
              } catch (err) {
                console.warn('⚠️ Could not fetch equipment names, using IDs:', err.message);
                const machinesFromIds = equipmentIds.map(id => ({
                  machineId: id,
                  machineName: `Equipment ${id}`
                }));
                setAvailableMachines(machinesFromIds);
              }
            } else {
              // No company ID: just use raw IDs
              const machinesFromIds = equipmentIds.map(id => ({
                machineId: id,
                machineName: `Equipment ${id}`
              }));
              setAvailableMachines(machinesFromIds);
            }
          } else {
            console.log('⚠️ No equipment_ids found in predictive maintenance response');
          }
        }

        // Mark initial data as loaded after first successful fetch
        if (!predictiveInitialDataLoaded) {
          setPredictiveInitialDataLoaded(true);
          console.log('✅ Initial predictive maintenance data loaded');
        }
      } else {
        console.warn('⚠️ Predictive maintenance API response missing or unsuccessful');
      }
    } catch (error) {
      console.error('❌ Error fetching predictive maintenance data:', error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [filters, predictiveInitialDataLoaded, activeTab, selectedSupplier, selectedSupplierEquipmentId, selectedItem, setAvailableMachines]);

  // Fetch predictive maintenance data - auto-trigger on initial load, manual trigger after that
  useEffect(() => {
    // Only fetch if Predictive Maintenance is the active tab
    if (activeTab !== 'Predictive Maintenance') {
      console.log('⏸️ Skipping Predictive Maintenance API call - not active tab');
      prevActiveTabRef.current = activeTab;
      return;
    }

    // Check if we just switched to Predictive Maintenance tab (was not Predictive Maintenance before)
    const justSwitchedToPredictiveMaintenance = prevActiveTabRef.current !== 'Predictive Maintenance' && activeTab === 'Predictive Maintenance';
    prevActiveTabRef.current = activeTab; // Update ref

    if (justSwitchedToPredictiveMaintenance) {
      console.log('🔄 Switched to Predictive Maintenance tab - triggering API call');
      setPredictiveInitialDataLoaded(false);
      initialCallMadeRef.current = false; // Reset initial call flag
      prevManualTriggerRef.current = manualTrigger; // Initialize ref

      // Only fetch if we have a company selected
      const companyId = filters.selectedCompany ||
        localStorage.getItem('companyId') ||
        sessionStorage.getItem('companyId');

      if (companyId) {
        fetchPredictiveMaintenanceData();
      } else {
        console.log('⏳ Waiting for company selection to fetch predictive maintenance data...');
      }
      return;
    }

    // Only fetch if we have a company selected
    const companyId = filters.selectedCompany ||
      localStorage.getItem('companyId') ||
      sessionStorage.getItem('companyId');

    if (!companyId) {
      console.log('⏳ Waiting for company selection to fetch predictive maintenance data...');
      return;
    }

    // On initial load (when Predictive Maintenance tab is first accessed), automatically trigger API with default date range
    if (!predictiveInitialDataLoaded) {
      // Check if we've already made the initial call
      if (initialCallMadeRef.current) {
        console.log('⏸️ Initial API call already made, skipping duplicate');
        return;
      }
      // Mark that initial call is being made (set synchronously before async operations)
      initialCallMadeRef.current = true;
      console.log('🔄 Initial load - automatically fetching predictive maintenance data with default date range');
      prevManualTriggerRef.current = manualTrigger; // Initialize ref
      fetchPredictiveMaintenanceData();
      return;
    }

    // Track previous selectedSupplier and selectedItem to detect changes
    const prevSelectedSupplier = prevSelectedSupplierRef.current;
    const prevSelectedSupplierEquipmentId = prevSelectedSupplierEquipmentIdRef.current;
    const supplierChanged = selectedSupplier !== prevSelectedSupplier || selectedSupplierEquipmentId !== prevSelectedSupplierEquipmentId;
    prevSelectedSupplierRef.current = selectedSupplier;
    prevSelectedSupplierEquipmentIdRef.current = selectedSupplierEquipmentId;

    const prevSelectedItem = prevSelectedItemRef.current;
    const itemChanged = selectedItem !== prevSelectedItem;
    prevSelectedItemRef.current = selectedItem;

    // After initial load, trigger API if manual trigger is activated OR if drill-down changed
    if (predictiveInitialDataLoaded) {
      // Check if manualTrigger actually changed or if drill-down was updated
      const triggerChanged = manualTrigger !== prevManualTriggerRef.current;

      if (!triggerChanged && !supplierChanged && !itemChanged) {
        console.log('⏸️ Skipping auto API call - waiting for manual trigger (arrow button) or drill-down');
        return;
      }

      // Update manual trigger ref
      prevManualTriggerRef.current = manualTrigger;

      if (supplierChanged || itemChanged) {
        console.log(`🔍 Drill-down changed (Supplier: ${selectedSupplier}, Item: ${selectedItem}) - proceeding with API call`);
      } else {
        console.log('🔵 Manual trigger activated - proceeding with API call');
      }
      fetchPredictiveMaintenanceData();
    }
  }, [
    filters.selectedCompany,
    filters.dateRange?.from?.getTime(), // Trigger when date range changes
    filters.dateRange?.to?.getTime(), // Trigger when date range changes
    filters.selectedMachine, // Include machine filter to ensure latest value is used
    filters.selectedLocation, // Include location filter to ensure latest value is used
    manualTrigger, // Trigger refresh when user clicks the arrow button
    activeTab, // Only fetch when Predictive Maintenance is the active tab
    selectedSupplier, // Trigger refresh when supplier drill-down changes
    selectedSupplierEquipmentId, // Trigger refresh when supplier equipment_id changes
    selectedItem, // Trigger refresh when item drill-down changes
    // NOTE: predictiveInitialDataLoaded intentionally NOT included to prevent double API calls
    fetchPredictiveMaintenanceData // Include fetchPredictiveMaintenanceData - isFetchingRef guard prevents duplicates
  ]);

  // Handler for supplier drill-down
  const handleSupplierClick = (supplierName, equipmentId) => {
    if (selectedSupplier === supplierName) {
      setSelectedSupplier(null);
      setSelectedSupplierEquipmentId(null);
      console.log('🔄 Clearing supplier drill-down');
    } else {
      setSelectedSupplier(supplierName);
      setSelectedSupplierEquipmentId(equipmentId);
      console.log('🔍 Applying supplier drill-down:', supplierName, 'equipment_id:', equipmentId);
    }
  };

  // Handler for item drill-down
  const handleItemClick = (itemName) => {
    if (selectedItem === itemName) {
      setSelectedItem(null);
      console.log('🔄 Clearing item drill-down');
    } else {
      setSelectedItem(itemName);
      console.log('🔍 Applying item drill-down:', itemName);
    }
  };

  // Calculate totals for items table
  const itemsTotalQty = items.reduce((sum, item) => sum + item.qty, 0);
  const itemsTotalCharge = items.reduce((sum, item) => sum + item.charge, 0);

  // Calculate totals for suppliers table
  const suppliersTotalQty = suppliers.reduce((sum, supplier) => sum + supplier.qty, 0);
  const suppliersTotalAmount = suppliers.reduce((sum, supplier) => sum + supplier.amount, 0);

  return (
    <div className="flex-1 overflow-auto">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl flex items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      )}
      <div className="p-6 space-y-6">
        {/* Key Metrics Cards Row */}
        <div className="flex gap-6 mb-6">
          {/* Remaining Useful Life */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 w-[248.5px] h-[131px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Remaining Useful Life</h3>
            <div className="flex flex-col">
              <div className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.remainingUsefulLife.value.toFixed(2)}
                <span className="text-base font-normal text-gray-600 ml-2">
                  {keyMetrics.remainingUsefulLife.unit}
                </span>
              </div>
            </div>
          </div>

          {/* Cost to Order */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 w-[248.5px] h-[131px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Cost to Order</h3>
            <div className="flex flex-col">
              <div className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.costToOrder.value.toFixed(2)}{keyMetrics.costToOrder.suffix}
                <span className="text-base font-normal text-gray-600 ml-2">
                  {keyMetrics.costToOrder.unit}
                </span>
              </div>
            </div>
          </div>

          {/* Number of Suppliers */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 w-[248.5px] h-[131px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Number of Suppliers</h3>
            <div className="flex flex-col">
              <div className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.numberOfSuppliers.value}
                <span className="text-base font-normal text-gray-600 ml-2">
                  {keyMetrics.numberOfSuppliers.unit}
                </span>
              </div>
            </div>
          </div>

          {/* Number of Spares */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 w-[248.5px] h-[131px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Number of Spares</h3>
            <div className="flex flex-col">
              <div className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.numberOfSpares.value}
                <span className="text-base font-normal text-gray-600 ml-2">
                  {keyMetrics.numberOfSpares.unit}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Data Tables Row */}
        <div className="flex gap-8">
          {/* Items Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 w-[520px] h-[315px] flex flex-col overflow-hidden">
            <table className="w-full table-fixed border-collapse" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr className="bg-blue-950 text-white" style={{ height: '36px' }}>
                  <th className="px-4 text-left text-xs font-semibold border-r border-blue-800">
                    <div className="flex justify-between items-center">
                      <span>Item Name</span>
                      {selectedItem && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem(null);
                          }}
                          className="text-[10px] bg-white text-blue-950 px-1.5 py-0.5 rounded hover:bg-gray-100 transition-colors"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="px-4 text-left text-xs font-semibold border-r border-blue-800">Reorder?</th>
                  <th className="px-4 text-right text-xs font-semibold border-r border-blue-800">Qty</th>
                  <th className="px-4 text-right text-xs font-semibold">Charge</th>
                </tr>
              </thead>
            </table>
            <div className="flex-1 overflow-y-auto" style={{ maxHeight: '252px' }}>
              <table className="w-full table-fixed border-collapse" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '40%' }} />
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '20%' }} />
                </colgroup>
                <tbody>
                  {Array.from({ length: Math.max(7, items.length) }).map((_, index) => {
                    const item = items[index];
                    return (
                      <tr
                        key={index}
                        className={`border-b border-gray-200 hover:bg-gray-50 cursor-pointer ${selectedItem === item?.itemName ? 'bg-blue-50 font-semibold' : ''
                          }`}
                        style={{ height: '36px' }}
                        onClick={() => item?.itemName && handleItemClick(item.itemName)}
                      >
                        <td className="px-4 text-xs text-gray-900 border-r border-gray-200">
                          <div className="flex items-center gap-2">
                            {item?.itemName || ''}
                            {selectedItem === item?.itemName && (
                              <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">Active</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 text-xs text-gray-900 border-r border-gray-200">{item?.reorderStatus || ''}</td>
                        <td className="px-4 text-xs text-gray-900 text-right border-r border-gray-200">{item?.qty || ''}</td>
                        <td className="px-4 text-xs text-gray-900 text-right">{item?.charge ? item.charge.toFixed(2) : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <table className="w-full table-fixed border-collapse" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <tfoot>
                <tr className="bg-blue-950 text-white font-semibold" style={{ height: '36px' }}>
                  <td className="px-4 text-xs border-r border-blue-800">Total</td>
                  <td className="px-4 text-xs border-r border-blue-800"></td>
                  <td className="px-4 text-xs text-right border-r border-blue-800">{itemsTotalQty}</td>
                  <td className="px-4 text-xs text-right">{itemsTotalCharge.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Suppliers Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 w-[515px] h-[315px] flex flex-col overflow-hidden">
            <table className="w-full table-fixed border-collapse" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr className="bg-blue-600 text-white" style={{ height: '36px' }}>
                  <th className="px-4 text-left text-xs font-semibold border-r border-blue-500">
                    <div className="flex justify-between items-center">
                      <span>Supplier Name</span>
                      {selectedSupplier && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSupplier(null);
                            setSelectedSupplierEquipmentId(null);
                          }}
                          className="text-[10px] bg-white text-blue-600 px-1.5 py-0.5 rounded hover:bg-blue-50 transition-colors"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="px-4 text-left text-xs font-semibold border-r border-blue-500">Reorder?</th>
                  <th className="px-4 text-right text-xs font-semibold border-r border-blue-500">Qty</th>
                  <th className="px-4 text-right text-xs font-semibold">Amount</th>
                </tr>
              </thead>
            </table>
            <div className="flex-1 overflow-y-auto" style={{ maxHeight: '252px' }}>
              <table className="w-full table-fixed border-collapse" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '40%' }} />
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '20%' }} />
                </colgroup>
                <tbody>
                  {Array.from({ length: Math.max(7, suppliers.length) }).map((_, index) => {
                    const supplier = suppliers[index];
                    return (
                      <tr
                        key={index}
                        className={`border-b border-gray-200 hover:bg-gray-50 cursor-pointer ${selectedSupplier === supplier?.supplierName ? 'bg-blue-50 font-semibold' : ''
                          }`}
                        style={{ height: '36px' }}
                        onClick={() => supplier?.supplierName && handleSupplierClick(supplier.supplierName, supplier.equipmentId)}
                      >
                        <td className="px-4 text-xs text-gray-900 border-r border-gray-200">
                          <div className="flex items-center gap-2">
                            {supplier?.supplierName || ''}
                            {selectedSupplier === supplier?.supplierName && (
                              <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">Active</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 text-xs text-gray-900 border-r border-gray-200">{supplier?.reorderStatus || ''}</td>
                        <td className="px-4 text-xs text-gray-900 text-right border-r border-gray-200">{supplier?.qty || ''}</td>
                        <td className="px-4 text-xs text-gray-900 text-right">{supplier?.amount ? supplier.amount.toFixed(2) : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <table className="w-full table-fixed border-collapse" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <tfoot>
                <tr className="bg-blue-600 text-white font-semibold" style={{ height: '36px' }}>
                  <td className="px-4 text-xs border-r border-blue-500">Total</td>
                  <td className="px-4 text-xs border-r border-blue-500"></td>
                  <td className="px-4 text-xs text-right border-r border-blue-500">{suppliersTotalQty}</td>
                  <td className="px-4 text-xs text-right">{suppliersTotalAmount.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictiveMaintenance;
